/**
 * The fields a reviewer can mark as disputed when contesting a claim. The key
 * is what `ContestModal` sends and what the server stores; the label is what a
 * person reads. Both the contest dialog and the claim's audit trail read this
 * list, so a field offered in one is named the same way in the other.
 */
export const CONTESTABLE_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "GrossRevenue", label: "Gross revenue" },
  { key: "CommissionPercent", label: "Commission percent" },
  { key: "ServiceDescription", label: "Service description" },
  { key: "RevenuePoolId", label: "Revenue pool" },
  { key: "LoanReference", label: "Loan reference" },
];

const LABEL_BY_KEY = new Map(CONTESTABLE_FIELDS.map((f) => [f.key, f.label]));

/**
 * Turns an audit entry's stored `contestedFields` into the text the audit trail
 * shows.
 *
 * The server stores the keys the contest sent as a JSON array, for example
 * `["GrossRevenue","CommissionPercent"]`. Each key is shown by its label, and a
 * key this list does not know is shown as it is. A value that is not a JSON
 * array of strings is shown the way the page always showed it: with the
 * brackets and quotes removed.
 */
export function formatContestedFields(stored: string | null | undefined): string {
  if (!stored) return "";
  try {
    const parsed: unknown = JSON.parse(stored);
    if (Array.isArray(parsed) && parsed.every((k) => typeof k === "string")) {
      return parsed.map((k: string) => LABEL_BY_KEY.get(k) ?? k).join(", ");
    }
  } catch {
    // Not JSON: fall through to the old display.
  }
  return stored.replace(/[\[\]"]/g, "");
}
